import React, { useState, useEffect } from "react";

import BounceLoader from "react-spinners/BounceLoader";
import { MdMarkEmailRead } from "react-icons/md";
import { BiSolidErrorAlt } from "react-icons/bi";
import { apiNewInquiry } from "../services/apiService";
import Button from "./Button";

const BACKEND_API = process.env.REACT_APP_BACKEND_API;
const calendlyLink = process.env.REACT_APP_CALENDLY_LINK;
const maxChars = 1000;

const ContactForm = () => {
  const [status, setStatus] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [error, setError] = useState(null);
  const [charCount, setCharCount] = useState(maxChars);
  const [inquirySent, setInquirySent] = useState(false);
  const [inquiryFailed, setInquiryFailed] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    if (status === 201) {
      setFormData({
        name: "",
        email: "",
        company: "",
        message: "",
      });
      setInquirySent(true);
      setStatus(null);
    }
    if (error !== null) {
      setInquiryFailed(true);
      setError(null);
    }
  }, [error, status]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
    if (name === "message") {
      setCharCount(maxChars - value.length);
    }
  };

  const handleSubmit = async () => {
    if (!formHasErrors()) {
      await apiCall(formData);
    } else {
      console.log("Form Has Errors");
    }
  };

  const apiCall = async (inquiry) => {
    setShowSpinner(true);
    const response = await apiNewInquiry(inquiry);
    console.log(response.status);
    if (response.hasError) {
      setError(
        error.response.data.message
          ? error.response.data.message
          : error.message
      );
    } else {
      setStatus(response.status);
    }
    setShowSpinner(false);
  };

  const formHasErrors = () => {
    let errors = false;
    const regex = new RegExp(
      "[a-zA-Z0-9.-_]{1,}@[a-zA-Z0-9.-]{1,}[.]{1}[a-zA-Z0-9]{2,}"
    );
    if (formData.name === "") {
      errors = true;
      setFormErrors((prevState) => ({
        ...prevState,
        name: "animate-shake border-red-600",
      }));
      clearErrors("name");
    } else if (formData.email === "" || !regex.test(formData.email)) {
      errors = true;
      setFormErrors((prevState) => ({
        ...prevState,
        email: "animate-shake border-red-600",
      }));
      clearErrors("email");
    } else if (formData.message === "") {
      errors = true;
      setFormErrors((prevState) => ({
        ...prevState,
        message: "animate-shake border-red-600",
      }));
      clearErrors("message");
    }
    return errors;
  };

  const clearErrors = (item) => {
    setTimeout(() => {
      setFormErrors((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 1000);
    clearTimeout();
  };

  return (
    <div className="w-full">
      <div className="text-center text-3xl md:text-4xl font-bold">
        Contact Us
      </div>
      <div className="flex justify-center text-xl text-center mt-10 px-5">
        We are standing by to help answer any question you may have.
      </div>
      <div className="w-full flex justify-center gap-x-5 mt-14">
        <div className="flex justify-center">
          <Button
            background="bg-snappily-orange"
            text="text-white"
            opacity="bg-opacity-100"
            title="Send a Message"
            height="h-20"
            width="w-40"
            borderHover="hover:border-snappily-orange"
            customFunc={() => {
              setShowEmail(!showEmail);
            }}
          />
        </div>
        <div className="flex justify-center">
          <a href={`${calendlyLink}`} target="_blank" rel="noopener noreferrer">
            <Button
              background="bg-snappily-orange"
              text="text-white"
              opacity="bg-opacity-100"
              title="Schedule a Call"
              height="h-20"
              width="w-40"
              borderHover="hover:border-snappily-orange"
              customFunc={() => {
                console.log(calendlyLink);
              }}
            />
          </a>
        </div>
      </div>
      {showEmail && (
        <>
          {showSpinner && (
            <div className="w-full bg-gray-700 opacity-40 fixed bottom-0">
              <div className="h-screen flex justify-center items-center text-white">
                <BounceLoader color="#FFFFFF" />
              </div>
            </div>
          )}
          {inquirySent && (
            <div className="flex justify-center w-full mt-10">
              <div className="grid justify-items-center text-center text-xl md:text-3xl font-bold w-3/5 md:w-2/5 mx-20 my-10 p-10 rounded-2xl border-2 border-black">
                <div>Thank You!</div>
                <div className="pt-5">Your message has been sent.</div>
                <MdMarkEmailRead className="text-6xl text-green-400 mt-5" />
              </div>
            </div>
          )}
          {inquiryFailed && (
            <div className="flex justify-center w-full">
              <div className="grid justify-items-center text-center text-lg md:text-3xl font-bold w-3/5 md:w-2/5 mx-20 my-10 p-5 rounded-2xl border-2 border-black">
                <div className="mt-10">
                  There was an error! Please try again later.
                </div>
                <BiSolidErrorAlt className="text-6xl text-red-400 my-10" />
              </div>
            </div>
          )}
          {!inquirySent && !inquiryFailed && (
            <>
              <div className="flex justify-center">
                <p className="text-center md:text-lg md:w-2/5 px-10 pt-10">
                  We do our best to respond to all inquiries within 60-minutes.
                </p>
              </div>
              <div className="flex justify-center">
                <form className="grid w-full md:w-2/5 sm:w-4/5">
                  <label className="grid px-5 pt-5">
                    <div>
                      Name:<span className="text-red-500">*</span>
                    </div>
                    <input
                      className={`border-1 border-black pl-2 py-2 rounded-xl ${formErrors.name}`}
                      type="text"
                      name="name"
                      value={formData.name}
                      placeholder="Name"
                      autoComplete="name"
                      onChange={handleChange}
                      maxLength="60"
                    />
                  </label>
                  <label className="grid px-5 pt-5">
                    <div>
                      Email:<span className="text-red-500">*</span>
                    </div>
                    <input
                      className={`border-1 border-black pl-2 py-2 rounded-xl ${formErrors.email}`}
                      type="email"
                      name="email"
                      value={formData.email}
                      placeholder="your@email.com"
                      autoComplete="emails"
                      onChange={handleChange}
                      maxLength="255"
                    />
                  </label>
                  <label className="grid px-5 pt-5">
                    Company Name:
                    <input
                      className="border-1 border-black pl-2 py-2 rounded-xl"
                      type="text"
                      name="company"
                      value={formData.company}
                      placeholder="Company Name"
                      autoComplete="organization"
                      onChange={handleChange}
                      maxLength="60"
                    />
                  </label>
                  <label className="grid px-5 py-5">
                    <div>
                      How can we help you:
                      <span className="text-red-500">*</span>
                    </div>
                    <textarea
                      className={`border-1 border-black p-2 py-2 h-48 text-justif rounded-xl ${formErrors.message}`}
                      type="text"
                      name="message"
                      value={formData.message}
                      placeholder="Write your question(s) here."
                      onChange={handleChange}
                      maxLength={maxChars}
                    />
                    <div className="flex justify-end mr-2">({charCount})</div>
                    <div className="flex justify-center pt-5">
                      <Button
                        background="bg-snappily-orange"
                        text="text-white"
                        opacity="bg-opacity-100"
                        title="Send"
                        height="h-14"
                        width="w-28"
                        borderHover="hover:border-snappily-orange"
                        customFunc={() => {
                          handleSubmit();
                        }}
                      />
                    </div>
                  </label>
                </form>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ContactForm;
